import axios, { AxiosResponse } from 'axios';
import {
  TalentCardData,
  ProcessingStatus,
  ScoringDimensions,
  CalibrationInsights,
  ChatMessage
} from '../types';
import { getApiUrl } from '../utils/runtimeConfig';
import { getIdToken } from './cognitoAuth';

// Base API configuration with runtime detection
const API_BASE_URL = getApiUrl();

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for authentication
apiClient.interceptors.request.use(
  async (config) => {
    // Add Cognito ID token to Authorization header (contains email claim)
    try {
      const idToken = await getIdToken();
      if (idToken) {
        config.headers.Authorization = `Bearer ${idToken}`;
      }

      // Add user email from localStorage as backup (from auth state)
      const authState = localStorage.getItem('talrev_auth');
      if (authState) {
        const auth = JSON.parse(authState);
        if (auth.email) {
          config.headers['X-User-Email'] = auth.email;
        }
      }
    } catch (error) {
      console.error('[API] Failed to get ID token:', error);
      // Continue with request even if token fetch fails
      // The backend will return 401 if authentication is required
    }

    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {

    if (error.response?.status === 401) {
      // Unauthorized - invalid or expired token
      const errorData = error.response.data;

      // Check if this is a token expiration error
      if (errorData?.detail?.includes('expired') || errorData?.detail?.includes('Token has expired')) {
        // Try to get a fresh token (getIdToken will refresh if needed)
        try {
          const newToken = await getIdToken();
          if (newToken && error.config) {
            // Retry the original request with new token
            error.config.headers.Authorization = `Bearer ${newToken}`;
            return apiClient.request(error.config);
          }
        } catch (refreshError) {
          console.error('[API] Token refresh failed:', refreshError);
        }
      }

      throw new Error('Authentication failed. Please sign in again.');
    } else if (error.response?.status === 403) {
      // Forbidden - insufficient permissions
      const errorData = error.response.data;
      if (typeof errorData === 'object' && errorData.detail) {
        throw new Error(errorData.detail);
      } else {
        throw new Error('You do not have permission to access this resource.');
      }
    } else if (error.response?.status === 404) {
      // Handle structured error response from backend
      const errorData = error.response.data;
      if (typeof errorData === 'object' && errorData.message) {
        throw new Error(errorData.message);
      } else if (typeof errorData === 'object' && errorData.error) {
        throw new Error(errorData.error);
      } else {
        throw new Error('Resource not found');
      }
    } else if (error.response?.status === 429) {
      // Handle rate limiting / concurrent upload limit
      const errorData = error.response.data;
      if (typeof errorData === 'object' && errorData.detail) {
        throw new Error(errorData.detail);
      } else {
        throw new Error('Too many requests. Please try again later.');
      }
    } else if (error.response?.status === 500) {
      throw new Error('Server error occurred');
    } else if (error.code === 'ECONNREFUSED') {
      throw new Error('Unable to connect to server. Please ensure the backend is running.');
    }

    return Promise.reject(error);
  }
);

class TalRevAPI {
  // Upload talent card file
  static async uploadTalentCard(
    file: File,
    onProgress?: (progress: number) => void,
    roleType?: 'individual_contributor' | 'manager',
    alias?: string
  ): Promise<{ id: string; message: string }> {
    const formData = new FormData();
    formData.append('file', file);

    // Add optional metadata
    if (roleType) {
      formData.append('role_type', roleType);
    }
    if (alias) {
      formData.append('alias', alias);
    }

    const response: AxiosResponse<{ session_id: string; status: string; message: string }> = await apiClient.post(
      '/api/upload',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total && onProgress) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(percentCompleted);
          }
        },
      }
    );

    if (!response.data.session_id) {
      throw new Error('Upload failed - no session ID returned');
    }

    return {
      id: response.data.session_id,
      message: response.data.message || 'Upload successful'
    };
  }

  // Get processing status
  static async getProcessingStatus(id: string): Promise<ProcessingStatus> {
    const response: AxiosResponse<{
      session_id: string;
      status: string;
      progress: number;
      employee_name?: string;
      current_stage: string;
      estimated_time_remaining?: number;
      error?: string;
      filename?: string;
    }> = await apiClient.get(`/api/processing/${id}`);

    if (!response.data.session_id) {
      throw new Error('Failed to get processing status');
    }

    // Transform backend response to frontend ProcessingStatus format
    const mapBackendStatusToFrontend = (backendStatus: string): 'uploaded' | 'processing' | 'completed' | 'error' => {
      switch (backendStatus) {
        case 'pending':
          return 'uploaded';
        case 'document_processing':
        case 'scoring_in_progress':
        case 'scoring':
          return 'processing';
        case 'completed':
          return 'completed';
        case 'failed':
        case 'error':
          return 'error';
        default:
          return 'processing'; // Default to processing for unknown states
      }
    };

    return {
      id: response.data.session_id,
      status: mapBackendStatusToFrontend(response.data.status),
      currentAgent: response.data.current_stage,
      progress: response.data.progress,
      currentStage: response.data.current_stage,
      estimatedTimeRemaining: response.data.estimated_time_remaining || 0,
      agentStatuses: [], // Backend doesn't provide this yet
      errors: response.data.error ? [response.data.error] : [],
      filename: response.data.filename,
    };
  }

  // Get talent card data and results
  static async getTalentCard(id: string): Promise<TalentCardData> {
    const response: AxiosResponse<{
      session_id: string;
      employee_profile: any;
      dimension_scores: any;
      calibration_insights: any;
      processing_metadata: any;
      summary: any;
      extracted_document_data?: any;
      status: string;
      processed_at?: string;
      uploaded_at?: string;
      file_name?: string;
      file_size?: number;
    }> = await apiClient.get(`/api/results/${id}`);

    if (!response.data.session_id) {
      throw new Error('Failed to get talent card data');
    }

    // Transform backend response to frontend TalentCardData format
    return {
      id: response.data.session_id,
      employeeProfile: {
        id: response.data.session_id,
        name: response.data.employee_profile?.name || 'Unknown',
        level: response.data.employee_profile?.level || 'Unknown',
        tenure: response.data.employee_profile?.tenure || 'Unknown',
        role: response.data.employee_profile?.role || 'Unknown',
        team: response.data.employee_profile?.team || 'Unknown',
        manager: response.data.employee_profile?.manager || 'Unknown',
        alias: response.data.employee_profile?.alias,
      },
      fileName: response.data.file_name || 'talent-card.pptx',
      fileSize: response.data.file_size || 0,
      uploadedAt: response.data.uploaded_at ? new Date(response.data.uploaded_at) : new Date(),
      processedAt: response.data.processed_at ? new Date(response.data.processed_at) : new Date(),
      extractedText: response.data.extracted_document_data?.raw_text || '',
      keyAccomplishments: response.data.employee_profile?.key_accomplishments || 
                         response.data.extracted_document_data?.employee_profile?.key_accomplishments || [],
      metrics: response.data.processing_metadata || {},
      status: response.data.status as 'uploaded' | 'processing' | 'completed' | 'error' || 'completed',
      scoringResults: response.data.dimension_scores ? 
        Object.keys(response.data.dimension_scores).reduce((acc, key) => {
          const dimensionData = response.data.dimension_scores[key];
          acc[key] = {
            ...dimensionData,
            reasoning: dimensionData.justification || dimensionData.reasoning || '',
            dimension: key
          };
          return acc;
        }, {} as ScoringDimensions) : undefined,
      calibrationInsights: response.data.calibration_insights,
      extractedDocumentData: response.data.extracted_document_data,
      processingMetadata: response.data.processing_metadata,
      summary: response.data.summary,
    };
  }

  // Get scoring results - integrated with getTalentCard
  static async getScoringResults(id: string): Promise<{
    scores: ScoringDimensions;
    insights: CalibrationInsights;
  }> {
    // Get results from the same endpoint as talent card data
    const talentCard = await this.getTalentCard(id);
    
    return {
      scores: talentCard.scoringResults as ScoringDimensions,
      insights: talentCard.calibrationInsights as CalibrationInsights,
    };
  }

  // Send chat message and get AI response
  static async sendChatMessage(
    talentCardId: string,
    message: string,
    context?: {
      dimension?: string;
      score?: number;
      evidence?: string[];
    }
  ): Promise<ChatMessage> {
    const response: AxiosResponse<{
      response: string;
      timestamp: string;
    }> = await apiClient.post('/api/chat', {
      message,
      session_id: talentCardId,
      context,
    });

    if (!response.data.response) {
      throw new Error('Failed to send chat message');
    }

    // Transform backend response to frontend ChatMessage format
    return {
      id: Date.now().toString(),
      type: 'assistant',
      content: response.data.response,
      timestamp: new Date(response.data.timestamp),
      context,
    };
  }

  // Get chat history - TODO: Backend endpoint not implemented yet
  static async getChatHistory(talentCardId: string): Promise<ChatMessage[]> {
    // Return empty array for now - backend doesn't have this endpoint yet
    return [];
  }

  // Export calibration report - TODO: Backend endpoint not implemented yet
  static async exportReport(id: string, format: 'pdf' | 'json' = 'pdf'): Promise<Blob> {
    const response = await apiClient.get(`/api/export/${id}?format=${format}`, {
      responseType: 'blob'
    });
    return response.data;
  }

  // Delete talent card
  static async deleteTalentCard(id: string): Promise<{
    message: string;
    deleted_employee: {
      id: string;
      name: string;
      level: string;
      role: string;
    };
    deletion_timestamp: string;
  }> {
    const response: AxiosResponse<{
      message: string;
      deleted_employee: {
        id: string;
        name: string;
        level: string;
        role: string;
      };
      deleted_files: string[];
      deleted_sessions: string[];
      deletion_timestamp: string;
    }> = await apiClient.delete(`/api/talent-cards/${id}`);

    return response.data;
  }

  // Download original talent card file
  static async downloadOriginalFile(sessionId: string): Promise<Blob> {
    const response: AxiosResponse<Blob> = await apiClient.get(
      `/api/download/${sessionId}`,
      {
        responseType: 'blob',
      }
    );

    return response.data;
  }

  // Health check
  static async healthCheck(): Promise<{ status: string; timestamp: string }> {
    const response: AxiosResponse<{
      status: string;
      timestamp: string;
    }> = await apiClient.get('/api/health');

    return response.data;
  }

  // List all talent cards for dashboard
  static async listTalentCards(): Promise<{
    talentCards: TalentCardData[];
    totalCount: number;
    activeCount: number;
    completedCount: number;
    failedCount: number;
  }> {
    const response: AxiosResponse<{
      talent_cards: any[];
      total_count: number;
      active_count: number;
      completed_count: number;
      failed_count: number;
    }> = await apiClient.get('/api/talent-cards');

    if (!response.data.talent_cards) {
      throw new Error('Failed to get talent cards list');
    }

    // Transform backend response to frontend format
    const talentCards = response.data.talent_cards.map(tc => {
      // Build scoring results from dimension_scores if available
      let scoringResults = undefined;
      if (tc.dimension_scores && typeof tc.dimension_scores === 'object') {
        scoringResults = Object.keys(tc.dimension_scores).reduce((acc, key) => {
          const dimensionData = tc.dimension_scores[key];
          acc[key] = {
            dimension: key,
            score: dimensionData.score || 0,
            confidence: dimensionData.confidence || 0,
            segments: [],
            reasoning: dimensionData.justification || dimensionData.reasoning || '',
            calibration: dimensionData.calibration
          };
          return acc;
        }, {} as any);
      }

      return {
        id: tc.id,
        employeeProfile: {
          id: tc.id,
          name: tc.employee_name || 'Processing...',
          level: tc.level || 'Unknown',
          tenure: tc.tenure || 'Unknown',
          role: tc.role || 'Unknown',
          team: tc.team || 'Unknown',
          manager: tc.manager || 'Unknown',
        },
        fileName: tc.file_name || 'talent-card.pptx',
        fileSize: 0,
        uploadedAt: new Date(tc.start_time),
        processedAt: tc.processed_date ? new Date(tc.processed_date) : (tc.status === 'completed' ? new Date(tc.start_time) : undefined),
        extractedText: '',
        keyAccomplishments: [],
        metrics: {
          average_score: tc.average_score,
          progress: tc.progress || 0,
          currentStage: tc.current_stage || 'Processing...',
          created_by: tc.created_by,
          user_id: tc.user_id
        },
        status: tc.status as 'uploaded' | 'processing' | 'completed' | 'error',
        scoringResults: scoringResults,
        calibrationInsights: tc.calibration_insights,
        processingDuration: tc.processing_duration,
        summary: {
          overall_assessment: tc.overall_assessment,
          key_strengths: tc.key_strengths,
          development_areas: tc.development_areas,
        },
        extractedDocumentData: tc.status === 'completed' ? {
          file_path: '',
          employee_profile: {
            name: tc.employee_name || 'Processing...',
            level: tc.level || 'Unknown',
            tenure: tc.tenure || 'Unknown',
            role: tc.role || 'Unknown',
            team: tc.team || 'Unknown',
            manager: tc.manager || 'Unknown',
            key_accomplishments: [],
            professional_experience: '',
            summary: '',
            tfcs: tc.tfcs || tc.technical_focus_areas || []
          },
          raw_text: '',
          slides_count: 0,
          extraction_metadata: {
            total_text_length: 0,
            processing_method: 'api',
            processing_time: '0s',
            model_id: 'unknown'
          }
        } : undefined,
      };
    });

    return {
      talentCards,
      totalCount: response.data.total_count,
      activeCount: response.data.active_count,
      completedCount: response.data.completed_count,
      failedCount: response.data.failed_count,
    };
  }
}

// Utility function for handling API errors in components
export const handleApiError = (error: any): string => {
  // First check for custom error messages from interceptor
  if (error.message) {
    return error.message;
  }
  
  // Handle structured error responses from backend
  const errorData = error.response?.data;
  if (typeof errorData === 'object') {
    if (errorData.message) {
      return errorData.message;
    }
    if (errorData.error) {
      return errorData.error;
    }
    if (errorData.detail && typeof errorData.detail === 'object' && errorData.detail.message) {
      return errorData.detail.message;
    }
    if (errorData.detail && typeof errorData.detail === 'string') {
      return errorData.detail;
    }
  }
  
  // Fallback for network errors
  if (error.code === 'ECONNREFUSED') {
    return 'Unable to connect to server. Please ensure the backend is running.';
  }
  
  return 'An unexpected error occurred';
};

export default TalRevAPI;